import {
  Hand,
  Banknote,
  CheckCheck,
  Contact,
  Loader,
  Pencil,
  Pointer,
  Send,
  XCircle,
  CheckCircle,
  MailCheck,
  CircleDotDashedIcon,
  FilePen,
  PenLine,
  FileText,
  FileSearch,
  Clapperboard,
  MonitorPlay,
  Upload,
  ScanSearch,
  Wallet,
  ClipboardCheckIcon,
  HandCoinsIcon,
  TriangleAlertIcon,
  FilePenLineIcon,
  CircleDollarSignIcon,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import type { CSSProperties } from "react";
import { Campaign, TypedStatusItem, BuiltInStatusValue, TailwindColor, Stage } from "@/types/types";
import { EvePipelinePhase } from "@/types/eve";
import { customStatusKind, customKeyForStatus, CUSTOM_STATE_LABELS, evePhaseKeyForStatus, evePayoutOrdinalLabel, allPhases, evePhaseOrder } from "@/lib/eve";

/**
 * A status item's color as ONE CSS variable, `--status-accent`, for the
 * surfaces that paint a task in its type's color beyond the badge: the Tasks
 * list's selected row, a detail panel's accents (`<Alert variant="accent">`,
 * borders, focus rings) and the creators-table task icon. They set this
 * `style` and use `bg-(--status-accent)/5`-style classes, so a task type's
 * color lives in exactly one place — the `color` of its STATUS_MAP entry.
 * Resolved from that entry's `text-<token>` class — and its `dark:text-<token>`
 * when it has one (lime-800 on light, lime-400 on dark): the style carries
 * both shades and index.css picks the theme's into `--status-accent`. Each
 * is `--color-<token>` for a palette token (`purple-500`), falling back to
 * the raw `--<token>` for a theme token (`warning`, `success`) — index.css
 * declares those with `@theme inline`, so Tailwind inlines them and emits no
 * `--color-*` for them.
 */
export function statusAccentStyle(status: string): CSSProperties {
  const color: string = STATUS_MAP[status as BuiltInStatusValue]?.color ?? "";
  const light = /(?:^|\s)text-([a-z]+(?:-\d{2,3})?)(?=\s|$)/.exec(color)?.[1] ?? "foreground";
  const dark = /(?:^|\s)dark:text-([a-z]+(?:-\d{2,3})?)(?=\s|$)/.exec(color)?.[1] ?? light;
  const shade = (token: string) => `var(--color-${token}, var(--${token}))`;
  return { "--status-accent-light": shade(light), "--status-accent-dark": shade(dark) } as CSSProperties;
}

export const STATUS_MAP: Record<BuiltInStatusValue, TypedStatusItem> = {
  "pending": {
    name: "Pending",
    value: "pending",
    description: "Press 'Start Outreach Sequence' to continue.",
    color: "bg-gray-500/10 text-gray-500" as TailwindColor,
    icon: <Pencil />,
  },
  "to-contact": {
    name: "To Contact",
    value: "to-contact",
    description: "Amy will outreach during scheduled active hours.",
    color: "bg-amber-500/10 text-amber-500" as TailwindColor,
    icon: <Contact />,
  },
  "pending-outreach": {
    name: "Pending Outreach",
    value: "pending-outreach",
    description: "Creator is in the queue for initial outreach.",
    color: "bg-sky-500/10 text-sky-500" as TailwindColor,
    icon: <Spinner />,
  },
  "outreached": {
    name: "Outreached",
    value: "outreached",
    description: "Initial outreach email has been sent.",
    color: "bg-blue-500/10 text-blue-500" as TailwindColor,
    icon: <Send />,
  },
  "creator-declined": {
    name: "Creator declined",
    value: "creator-declined",
    description: "Creator decliend to partner.",
    color: "bg-red-500/10 text-red-500" as TailwindColor,
    icon: <Hand />,
  },
  "negotiating": {
    name: "Negotiating",
    value: "negotiating",
    description: "Currently in negotiation with the creator.",
    color: "bg-emerald-500/10 text-emerald-500" as TailwindColor,
    icon: <Banknote />,
  },
  "waiting-approval": {
    name: "Waiting Approval",
    value: "waiting-approval",
    description: "Approve or Decline them.",
    color: "bg-indigo-500/10 text-indigo-500" as TailwindColor,
    icon: <Pointer />,
  },
  "pending-rejection": {
    name: "Pending Rejection",
    value: "pending-rejection",
    description: "Amy will send the rejection message soon.",
    color: "bg-orange-500/10 text-orange-500" as TailwindColor,
    icon: <Spinner className="size-4" />,
  },
  "rejected": {
    name: "Rejected",
    value: "rejected",
    description: "Creator was rejected by you.",
    color: "bg-rose-500/10 text-rose-500" as TailwindColor,
    icon: <XCircle />,
  },
  "pending-activation": {
    name: "Pending Activation",
    value: "pending-activation",
    description: "Amy will send the activation message soon.",
    color: "bg-emerald-500/10 text-emerald-500" as TailwindColor,
    icon: <Spinner className="size-4" />,
  },
  "activated": {
    name: "Activated",
    value: "activated",
    description: "Amy sent the activation message.",
    color: "bg-green-500/10 text-green-500" as TailwindColor,
    icon: <CheckCircle />,
  },
  "waiting-for-content": {
    name: "Waiting for content",
    value: "waiting-for-content",
    description: "Just approved the creator, waiting for content for action.",
    color: "bg-cyan-500/10 text-cyan-500" as TailwindColor,
    icon: <Loader />,
  },
  "uploaded": {
    name: "Uploaded",
    value: "uploaded",
    description: "Creator has uploaded or scheduled their content.",
    color: "bg-pink-500/10 text-pink-500" as TailwindColor,
    icon: <CheckCheck />,
  },
  "handoff": {
    name: "Handoff",
    value: "handoff",
    description: "Creator thread is handed off to you.",
    color: "bg-warning/10 text-warning" as TailwindColor,
    icon: <Hand />,
  },
  "next-message": {
    name: "Next Message",
    value: "next-message",
    description: "Review and send the message to the creator.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <MailCheck />,
  },
  // Tasks-page pseudo statuses (Amy-v2 needs-attention rows — like
  // handoff/next-message these never appear as creator statuses).
  "phase-review": {
    name: "Review",
    value: "phase-review",
    description: "A submission is waiting for your review verdict.",
    color: "bg-indigo-500/10 text-indigo-500" as TailwindColor,
    icon: <ClipboardCheckIcon />,
  },
  "payout-confirm": {
    name: "Payout",
    value: "payout-confirm",
    description: "A prepared payout is waiting for your confirmation.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <HandCoinsIcon />,
  },
  "eve-attention": {
    name: "Needs Attention",
    value: "eve-attention",
    description: "Amy flagged this creator for a look.",
    color: "bg-warning/10 text-warning" as TailwindColor,
    icon: <TriangleAlertIcon />,
  },
  "contract-change": {
    name: "Contract Change",
    value: "contract-change",
    description: "A creator asked to change their contract — approve or decline Amy's proposed wording.",
    color: "bg-purple-500/10 text-purple-500" as TailwindColor,
    icon: <FilePenLineIcon />,
  },
  "offer-review": {
    name: "Set Offer",
    value: "offer-review",
    description: "Amy is about to make a creator an offer — set it or confirm the campaign's terms and she continues.",
    color: "bg-lime-800/10 text-lime-800 dark:bg-lime-400/10 dark:text-lime-400" as TailwindColor,
    icon: <CircleDollarSignIcon />,
  },
  "performance-payment": {
    name: "Performance Payment",
    value: "performance-payment",
    description: "A view-based performance payment has been calculated and is due.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Banknote />,
  },
  "completed": {
    name: "Completed",
    value: "completed",
    description: "All custom statuses completed successfully.",
    color: "bg-emerald-500/10 text-emerald-500" as TailwindColor,
    icon: <CheckCheck />,
  },
  // ── Amy-v2 phase-pipeline statuses (amy/lib/domain/statuses.ts) ──
  "pending-contract": {
    name: "Pending Contract",
    value: "pending-contract",
    description: "Amy is sending the contract for signature.",
    color: "bg-violet-500/10 text-violet-500" as TailwindColor,
    icon: <FilePen />,
  },
  "waiting-signature": {
    name: "Waiting Signature",
    value: "waiting-signature",
    description: "Contract sent — waiting for the creator's signature.",
    color: "bg-purple-500/10 text-purple-500" as TailwindColor,
    icon: <PenLine />,
  },
  "waiting-script": {
    name: "Waiting Script",
    value: "waiting-script",
    description: "Waiting for the creator to submit their script.",
    color: "bg-cyan-500/10 text-cyan-500" as TailwindColor,
    icon: <FileText />,
  },
  "script-review": {
    name: "Script Review",
    value: "script-review",
    description: "Script submitted — awaiting the review verdict.",
    color: "bg-indigo-500/10 text-indigo-500" as TailwindColor,
    icon: <FileSearch />,
  },
  "waiting-draft": {
    name: "Waiting Draft",
    value: "waiting-draft",
    description: "Waiting for the creator to submit their draft video.",
    color: "bg-blue-500/10 text-blue-500" as TailwindColor,
    icon: <Clapperboard />,
  },
  "draft-review": {
    name: "Draft Review",
    value: "draft-review",
    description: "Draft submitted — awaiting the review verdict.",
    color: "bg-indigo-500/10 text-indigo-500" as TailwindColor,
    icon: <MonitorPlay />,
  },
  "waiting-posting": {
    name: "Waiting Posting",
    value: "waiting-posting",
    description: "Waiting for the creator to publish the post.",
    color: "bg-teal-500/10 text-teal-500" as TailwindColor,
    icon: <Upload />,
  },
  "posting-review": {
    name: "Posting Review",
    value: "posting-review",
    description: "Post link submitted — awaiting the review verdict.",
    color: "bg-indigo-500/10 text-indigo-500" as TailwindColor,
    icon: <ScanSearch />,
  },
  "pending-payment-1": {
    name: "Pending Payment 1",
    value: "pending-payment-1",
    description: "Amy is preparing the first split payout for confirmation.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Wallet />,
  },
  "waiting-payment-1": {
    name: "Waiting Payment 1",
    value: "waiting-payment-1",
    description: "First split payout confirmed — waiting for the creator to claim it.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Banknote />,
  },
  "pending-payment-2": {
    name: "Pending Payment 2",
    value: "pending-payment-2",
    description: "Amy is preparing the second split payout for confirmation.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Wallet />,
  },
  "waiting-payment-2": {
    name: "Waiting Payment 2",
    value: "waiting-payment-2",
    description: "Second split payout confirmed — waiting for the creator to claim it.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Banknote />,
  },
  "pending-payment": {
    name: "Pending Payment",
    value: "pending-payment",
    description: "Amy is preparing the payout for confirmation.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Wallet />,
  },
  "waiting-payment": {
    name: "Waiting Payment",
    value: "waiting-payment",
    description: "Payout confirmed — waiting for the creator to claim it.",
    color: "bg-success/10 text-success" as TailwindColor,
    icon: <Banknote />,
  },
};

const DEFAULT_CUSTOM_STATUS_COLOR = "bg-violet-500/10 text-violet-500";

const CUSTOM_STATE_DESCRIPTIONS: Record<string, string> = {
  entry: "Waiting on the creator for this step.",
  review: "Submission awaiting the review verdict.",
  run: "Amy is running this step on our side.",
  wait: "Held until the matching event arrives.",
};

/**
 * `pipeline` is the RESOLVED phases array a label should read — for a creator
 * row that is their tier's pipeline (lib/eve.ts pipelineForCreator), for a
 * campaign-wide label the union across tiers (allPhases). Labels and payout
 * ordinals differ per tier, so callers never pass a campaign's raw field.
 */
export function resolveStatusItem(
  status: string,
  stages?: Stage[],
  pipeline?: EvePipelinePhase[] | null,
): TypedStatusItem | null {
  const builtIn = STATUS_MAP[status as BuiltInStatusValue];
  if (builtIn) {
    // Split payouts: with ≥2 payment phases enabled, the payout statuses
    // display their ordinal ("Pending Payment 2" for the final slot of a
    // two-way split). Single-payment campaigns keep the plain names.
    const payoutKey = evePhaseKeyForStatus(status);
    const ordinal = payoutKey ? evePayoutOrdinalLabel(payoutKey, pipeline) : null;
    if (ordinal) {
      const stateWord = status.startsWith("pending-") ? "Pending" : "Waiting";
      return { ...builtIn, name: `${stateWord} ${ordinal}` };
    }
    return builtIn;
  }

  // Amy-v2 custom phase shapes resolve BEFORE stage UUIDs (stage ids are
  // hex — they can never start with x-). Label = the campaign's pipeline
  // entry label + the state suffix, mirroring amy's resolveStatusLabel.
  const kind = customStatusKind(status);
  if (kind) {
    const key = customKeyForStatus(status)!;
    const entry = pipeline?.find((p) => p.key === key);
    return {
      name: `${entry?.label ?? key}: ${CUSTOM_STATE_LABELS[kind]}`,
      value: status,
      description: CUSTOM_STATE_DESCRIPTIONS[kind],
      color: DEFAULT_CUSTOM_STATUS_COLOR as TailwindColor,
      icon: <CircleDotDashedIcon />,
    };
  }

  if (stages) {
    const stage = stages.find((s) => s.id === status);
    if (stage) {
      return {
        name: stage.name || "Untitled Status",
        value: stage.id,
        description: stage.description || "",
        color: (stage.color || DEFAULT_CUSTOM_STATUS_COLOR) as TailwindColor,
        icon: <CircleDotDashedIcon />,
      };
    }
  }

  return null;
}

/**
 * Display name for a status when rows span several campaigns (the Global CRM
 * table) or several tiers (the status filter): the first campaign whose
 * stages / pipelines know the value names it — every tier's phases count
 * (allPhases), since the label is campaign-wide here; built-ins resolve
 * without one, unknown values fall back to the raw slug.
 */
export function resolveStatusName(
  status: string,
  campaigns: ReadonlyArray<Pick<Campaign, "stages" | "partnership" | "eve_tier_pipelines" | "is_eve">>,
): string {
  for (const campaign of campaigns) {
    const item = resolveStatusItem(
      status,
      campaign.is_eve ? undefined : campaign.stages,
      allPhases(campaign),
    );
    if (item) return item.name;
  }
  return resolveStatusItem(status)?.name ?? status;
}

/**
 * The statuses before the post-activation pipeline, in campaign-flow order:
 * search → outreach → negotiation → the approval decision, its two "no"
 * outcomes first, then the yes path to activation. Amy-v2's contract is the
 * one phase that runs BEFORE the activation email (amy phases.ts), so its
 * statuses live here rather than with the ordered phases.
 */
const FLOW_BEFORE_PIPELINE: string[] = [
  "pending",
  "to-contact",
  "pending-outreach",
  "outreached",
  "negotiating",
  "waiting-approval",
  "creator-declined",
  "pending-rejection",
  "rejected",
  "pending-contract",
  "waiting-signature",
  "pending-activation",
  "activated",
  "waiting-for-content",
  "uploaded",
];
const COMPLETED_RANK = 1e9;
const UNKNOWN_RANK = 2e9;

/**
 * Sort key for where a status happens in a campaign (lower = earlier), for
 * lists that read in flow order instead of A–Z (the creators-table status
 * filter). After FLOW_BEFORE_PIPELINE comes the post-activation pipeline in
 * its configured order — Amy-v2 phases (custom x- ones included) by phase
 * order, the creator's step (waiting-<key>, pending-payment) before the
 * verdict / claim (…-review, waiting-payment); legacy stages by stage order —
 * then completed, then anything unrecognised. Like resolveStatusName, the
 * first campaign that knows a phase or stage places it.
 */
export function resolveStatusRank(
  status: string,
  campaigns: ReadonlyArray<Pick<Campaign, "stages" | "partnership" | "eve_tier_pipelines" | "is_eve">>,
): number {
  const fixed = FLOW_BEFORE_PIPELINE.indexOf(status);
  if (fixed !== -1) return fixed;
  if (status === "completed") return COMPLETED_RANK;

  const pipelineStart = FLOW_BEFORE_PIPELINE.length;
  const phaseKey = evePhaseKeyForStatus(status);
  if (phaseKey) {
    const entry = campaigns.map((c) => allPhases(c).find((p) => p.key === phaseKey)).find(Boolean);
    const step = status.endsWith("-review") || status.startsWith("waiting-payment") ? 0.5 : 0;
    return pipelineStart + evePhaseOrder(entry ?? { key: phaseKey }) + step;
  }
  for (const campaign of campaigns) {
    if (campaign.is_eve) continue;
    const stage = campaign.stages?.find((s) => s.id === status);
    if (stage) return pipelineStart + stage.order;
  }
  return UNKNOWN_RANK;
}

// Post-activation test for analytics: the legacy predicate was
// activated/completed OR not-in-STATUS_MAP (stage UUIDs). Phase statuses now
// ARE in STATUS_MAP, so they must be listed explicitly — script and later
// run post-activation; the contract phase sits BETWEEN approval and
// activation and is deliberately absent.
const POST_ACTIVATION_BUILTINS = new Set<string>([
  "activated",
  "completed",
  "waiting-script",
  "script-review",
  "waiting-draft",
  "draft-review",
  "waiting-posting",
  "posting-review",
  "pending-payment-1",
  "waiting-payment-1",
  "pending-payment-2",
  "waiting-payment-2",
  "pending-payment",
  "waiting-payment",
]);

export function isPostActivationStatus(status: string): boolean {
  if (POST_ACTIVATION_BUILTINS.has(status)) return true;
  if (customStatusKind(status)) return true; // custom phases run post-activation
  return !(status in STATUS_MAP); // stage UUIDs / unknown
}